import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import type {
  FactorRosterResponse,
  FactorRosterSort,
  FactorRosterSortDir,
  FactorRosterSourceFilter,
  FactorRosterStatusFilter,
  RosterCombineOverrides,
} from '../../../types/statusFactors';
import { ROLLUP_SIMULATION_DEBOUNCE_MS } from '../FactorDetailPane/useRollupSimulation';

export const ROSTER_PAGE_SIZE = 50;
const ROSTER_SEARCH_DEBOUNCE_MS = 300;

export interface FactorRosterQuery {
  q: string;
  status: FactorRosterStatusFilter;
  source: FactorRosterSourceFilter;
  sort: FactorRosterSort;
  dir: FactorRosterSortDir;
  offset: number;
}

const DEFAULT_QUERY: FactorRosterQuery = {
  q: '',
  status: 'all',
  source: 'all',
  sort: 'severity',
  dir: 'desc',
  offset: 0,
};

/** `all` and an empty search are the absence of a filter, so they never reach the route. */
function toParams(query: FactorRosterQuery, overrides?: RosterCombineOverrides): Record<string, string | number> {
  const params: Record<string, string | number> = {
    limit: ROSTER_PAGE_SIZE,
    offset: query.offset,
    sort: query.sort,
    dir: query.dir,
  };
  if (query.q) params.q = query.q;
  if (query.status !== 'all') params.status = query.status;
  if (query.source !== 'all') params.source = query.source;
  if (overrides) {
    if (overrides.aggregationMethod !== undefined) params.aggregationMethod = overrides.aggregationMethod;
    if (overrides.weighting !== undefined) params.weighting = overrides.weighting;
    // The route reads `none` as "no floor"; absence would mean "keep the stored floor".
    if (overrides.influenceFloor !== undefined) params.influenceFloor = overrides.influenceFloor ?? 'none';
  }
  return params;
}

export interface UseFactorRosterResult {
  data: FactorRosterResponse | null;
  loading: boolean;
  error: Error | null;
  query: FactorRosterQuery;
  /** The search box's text, ahead of the debounced `query.q`. */
  searchText: string;
  /**
   * Set the box's text and schedule the debounced fetch — the raw `setSearchText` state setter
   * updates immediately, then the debouncer commits a `q` change into the query (which the fetch
   * effect reacts to) after `ROSTER_SEARCH_DEBOUNCE_MS`.
   */
  commitSearchText: (text: string) => void;
  setStatus: (status: FactorRosterStatusFilter) => void;
  setSource: (source: FactorRosterSourceFilter) => void;
  setSort: (sort: FactorRosterSort, dir: FactorRosterSortDir) => void;
  nextPage: () => void;
  previousPage: () => void;
  /** Jumps to the page starting at `offset`. */
  setOffset: (offset: number) => void;
  /** Resets search, status and source to their defaults (sort stays) and refetches from page one. */
  clearFilters: () => void;
  /** True while search, status or source narrow the roster. */
  filtersActive: boolean;
  refetch: () => void;
  /** Lifts an exclusion and refetches. A 404 (already lifted elsewhere) is the outcome Undo wanted, so it counts as success. */
  restoreExclusion: (memberSphereId: string) => Promise<void>;
  /** Saves (or, when empty, clears) an exclusion's reason and refetches. Resolves `gone` when the exclusion no longer exists. */
  setExclusionReason: (memberSphereId: string, reason: string) => Promise<'saved' | 'gone'>;
  /** Saves a member's influence on this factor and refetches; rejects when the save fails so the cell can revert. */
  setInfluence: (memberSphereId: string, influence: number) => Promise<void>;
  /** True when the server refused or could not verify the unsaved combine settings, so the rows are the stored roster's. */
  overridesUnverified: boolean;
}

const statusOf = (error: unknown) => (error as { response?: { status?: number } })?.response?.status;
const isNotFound = (error: unknown) => (error as { response?: { status?: number } })?.response?.status === 404;

/**
 * Fetches one factor's roster. Every filter, sort and page change is a route-param change —
 * rows are never filtered, sorted or sliced here, and a filter change resets the offset.
 */
export function useFactorRoster(
  sphereId: string | null,
  factorId: string | null,
  overrides?: RosterCombineOverrides
): UseFactorRosterResult {
  const apiClients = useApiClientsOrNull();
  const graphClient = apiClients?.graphClient;
  const [query, setQuery] = useState<FactorRosterQuery>(DEFAULT_QUERY);
  const [searchText, setSearchText] = useState('');
  const [data, setData] = useState<FactorRosterResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const overridesKey = overrides ? JSON.stringify(overrides) : '';
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;
  // The override key the last request carried; a request whose overrides changed waits out the preview's debounce.
  const requestedKeyRef = useRef(overridesKey);
  // The override key whose response fell back to the saved roster (403 or 503); it drives the "unverified" notice.
  // Only a 403 also sticks in `rejectedKeyRef`: a viewer stays a viewer, while a 503 is transient and the next
  // fetch for the same key tries the overrides again.
  const [rejectedKey, setRejectedKey] = useState('');
  const rejectedKeyRef = useRef('');
  const overridesUnverified = overridesKey !== '' && rejectedKey === overridesKey;

  // A different factor is a different roster: drop filters, search text and the previous factor's rows
  // during render so no fetch runs with the old factor's query.
  const scopeKey = `${sphereId ?? ''}/${factorId ?? ''}`;
  const [renderedScopeKey, setRenderedScopeKey] = useState(scopeKey);
  if (renderedScopeKey !== scopeKey) {
    setRenderedScopeKey(scopeKey);
    setQuery(DEFAULT_QUERY);
    setSearchText('');
    setData(null);
    setError(null);
  }

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, [scopeKey]);

  useEffect(() => {
    if (!graphClient || !sphereId || !factorId) return undefined;
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const send = (withOverrides: boolean): Promise<FactorRosterResponse> =>
      graphClient.get<FactorRosterResponse>(`/spheres/${sphereId}/factors/${factorId}/roster`, {
        params: toParams(query, withOverrides ? overridesRef.current : undefined),
        signal: controller.signal,
      });
    const run = () => {
      requestedKeyRef.current = overridesKey;
      const useOverrides = overridesKey !== '' && rejectedKeyRef.current !== overridesKey;
      let fellBack = false;
      send(useOverrides)
        .catch((err: unknown) => {
          const status = statusOf(err);
          if (!useOverrides || cancelled || (status !== 403 && status !== 503)) throw err;
          fellBack = true;
          if (status === 403) rejectedKeyRef.current = overridesKey;
          setRejectedKey(overridesKey);
          return send(false);
        })
        .then((response) => {
          if (cancelled) return;
          if (useOverrides && !fellBack) setRejectedKey('');
          // A removal can empty the last page; land on the last page that still has rows, never on an empty sentence.
          if (response.members.length === 0 && query.offset > 0) {
            const lastOffset = response.total > 0 ? Math.floor((response.total - 1) / ROSTER_PAGE_SIZE) * ROSTER_PAGE_SIZE : 0;
            if (lastOffset < query.offset) {
              setQuery((current) => (current.offset === query.offset ? { ...current, offset: lastOffset } : current));
              return;
            }
          }
          setData(response);
        })
        .catch((err: unknown) => {
          if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    const debounced = overridesKey !== '' && overridesKey !== requestedKeyRef.current;
    const timer = debounced ? setTimeout(run, ROLLUP_SIMULATION_DEBOUNCE_MS) : null;
    if (!timer) run();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      controller.abort();
    };
  }, [graphClient, sphereId, factorId, query, reloadToken, overridesKey]);

  const commitSearchText = useCallback((text: string) => {
    setSearchText(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setQuery((current) => (current.q === text ? current : { ...current, q: text, offset: 0 }));
    }, ROSTER_SEARCH_DEBOUNCE_MS);
  }, []);

  const setStatus = useCallback(
    (status: FactorRosterStatusFilter) => setQuery((current) => ({ ...current, status, offset: 0 })),
    []
  );
  const setSource = useCallback(
    (source: FactorRosterSourceFilter) => setQuery((current) => ({ ...current, source, offset: 0 })),
    []
  );
  const setSort = useCallback(
    (sort: FactorRosterSort, dir: FactorRosterSortDir) => setQuery((current) => ({ ...current, sort, dir, offset: 0 })),
    []
  );
  const nextPage = useCallback(
    () => setQuery((current) => ({ ...current, offset: current.offset + ROSTER_PAGE_SIZE })),
    []
  );
  const previousPage = useCallback(
    () => setQuery((current) => ({ ...current, offset: Math.max(0, current.offset - ROSTER_PAGE_SIZE) })),
    []
  );
  const setOffset = useCallback(
    (offset: number) => setQuery((current) => ({ ...current, offset: Math.max(0, offset) })),
    []
  );
  const clearFilters = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchText('');
    setQuery((current) => ({ ...current, q: '', status: 'all', source: 'all', offset: 0 }));
  }, []);
  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  const restoreExclusion = useCallback(
    async (memberSphereId: string) => {
      if (!graphClient || !sphereId || !factorId) throw new Error('graph client unavailable');
      try {
        await graphClient.delete(`/spheres/${sphereId}/factors/${factorId}/exclusions/${memberSphereId}`);
      } catch (error) {
        if (!isNotFound(error)) throw error;
      }
      refetch();
    },
    [graphClient, sphereId, factorId, refetch]
  );

  const setExclusionReason = useCallback(
    async (memberSphereId: string, reason: string) => {
      if (!graphClient || !sphereId || !factorId) throw new Error('graph client unavailable');
      try {
        await graphClient.patch(`/spheres/${sphereId}/factors/${factorId}/exclusions/${memberSphereId}`, { reason });
      } catch (error) {
        if (!isNotFound(error)) throw error;
        refetch();
        return 'gone' as const;
      }
      refetch();
      return 'saved' as const;
    },
    [graphClient, sphereId, factorId, refetch]
  );

  const setInfluence = useCallback(
    async (memberSphereId: string, influence: number) => {
      if (!graphClient || !sphereId || !factorId) throw new Error('graph client unavailable');
      await graphClient.patch(`/spheres/${sphereId}/factors/${factorId}/members/${memberSphereId}`, { influence });
      refetch();
    },
    [graphClient, sphereId, factorId, refetch]
  );

  return {
    data,
    loading,
    error,
    query,
    searchText,
    commitSearchText,
    setStatus,
    setSource,
    setSort,
    nextPage,
    previousPage,
    setOffset,
    clearFilters,
    filtersActive: query.q !== '' || query.status !== 'all' || query.source !== 'all',
    refetch,
    restoreExclusion,
    setExclusionReason,
    setInfluence,
    overridesUnverified,
  };
}
